/**
 * lh-cart-gift-wrap.js
 * Lolahome — Embrulho para Presente no carrinho lateral (Velour Theme)
 *
 * Estratégia de persistência:
 *  - Listeners no `document` (delegação) — sobrevivem a qualquer re-render do DOM
 *  - MutationObserver no #CartDrawer — detecta re-renders e restaura o estado
 *  - localStorage — guarda se está marcado + texto da nota
 */

(function () {
  'use strict';

  // ─── Configuração ────────────────────────────────────────────────────────────
  const STORAGE_KEY     = 'lh_gift_wrap_state';
  const ACCORDION_STORAGE_KEY = 'lh_gift_wrap_accordion_open';
  const PRODUCT_ID      = 34202830503981;
  const ACCORDION_GROUP = 'lh-cart-extras';

  // ─── Estado no localStorage ──────────────────────────────────────────────────
  function loadState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { checked: false, note: '' };
    } catch {
      return { checked: false, note: '' };
    }
  }

  function saveState(checked, note) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ checked, note: note || '' }));
  }

  // ─── Referências ao DOM (sempre re-buscadas, DOM pode ser substituído) ───────
  const getTrigger  = () => document.getElementById('lh-gift-wrap-trigger');
  const getCheckbox = () => document.getElementById('lh-gift-wrap-checkbox');
  const getTextarea = () => document.getElementById('lh-gift-wrap-note');

  // ─── Estado aberto/fechado do accordion (localStorage) ──────────────────────
  function saveAccordionOpen(open) {
    localStorage.setItem(ACCORDION_STORAGE_KEY, open ? '1' : '0');
  }

  function isAccordionSavedOpen() {
    return localStorage.getItem(ACCORDION_STORAGE_KEY) === '1';
  }

  // ─── Controle do accordeon ───────────────────────────────────────────────────
  function openAccordion(shouldFocus) {
    const trigger = getTrigger();
    if (!trigger) return;

    trigger.classList.add('wt-collapse__trigger--active');
    trigger.setAttribute('aria-expanded', 'true');

    // Fecha os outros accordeões do mesmo grupo (collapsible-section nativos)
    document
      .querySelectorAll(`collapsible-section[data-accordion-set="${ACCORDION_GROUP}"] .wt-collapse__trigger`)
      .forEach(function (t) {
        if (t.classList.contains('wt-collapse__trigger--active')) {
          t.click();
        }
      });

    if (shouldFocus) {
      // Aguarda a animação de abertura (500ms) antes de focar
      setTimeout(function () {
        getTextarea()?.focus();
      }, 550);
    }
  }

  function closeAccordion() {
    const trigger = getTrigger();
    if (!trigger) return;
    trigger.classList.remove('wt-collapse__trigger--active');
    trigger.setAttribute('aria-expanded', 'false');
  }

  function isAccordionOpen() {
    return getTrigger()?.classList.contains('wt-collapse__trigger--active') ?? false;
  }

  // ─── Operações no carrinho ───────────────────────────────────────────────────
  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: false }));
  }

  async function addGiftWrap() {
    try {
      const resp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items: [{ id: PRODUCT_ID, quantity: 1 }] }),
      });
      if (!resp.ok) throw new Error(resp.statusText);
      refreshCart();
    } catch (err) {
      console.error('[LH Gift Wrap] Erro ao adicionar produto:', err);
    }
  }

  async function removeGiftWrap() {
    try {
      const resp = await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ updates: { [PRODUCT_ID]: 0 } }),
      });
      if (!resp.ok) throw new Error(resp.statusText);
      refreshCart();
    } catch (err) {
      console.error('[LH Gift Wrap] Erro ao remover produto:', err);
    }
  }

  // ─── Ações do utilizador ─────────────────────────────────────────────────────
  function activateGiftWrap() {
    const note = getTextarea()?.value || loadState().note || '';
    saveState(true, note);
    openAccordion(true); // abre + foca o textarea
    saveAccordionOpen(true);
    addGiftWrap();
  }

  function deactivateGiftWrap() {
    saveState(false, '');
    closeAccordion();
    saveAccordionOpen(false);
    removeGiftWrap();
  }

  // ─── Verifica se o produto de embrulho está no DOM do carrinho ──────────────
  function isGiftWrapInCart() {
    // O tema Velour renderiza cada item com data-quantity-variant-id no counter
    return !!document.querySelector(
      `[data-quantity-variant-id="${PRODUCT_ID}"]`
    );
  }

  // ─── Restaurar estado após re-render ─────────────────────────────────────────
  function restoreState() {
    const trigger  = getTrigger();
    const checkbox = getCheckbox();
    const textarea = getTextarea();
    if (!trigger || !checkbox || !textarea) return;

    let state = loadState();

    // Sincroniza o localStorage com o estado real do carrinho.
    const inCart = isGiftWrapInCart();

    // Produto removido do carrinho sem usar o checkbox → desmarca
    if (state.checked && !inCart) {
      state = { checked: false, note: '' };
      saveState(false, '');
    }

    // Produto presente no carrinho mas checkbox desmarcado → marca
    if (!state.checked && inCart) {
      state.checked = true;
      saveState(true, state.note);
    }

    checkbox.checked = state.checked;
    if (state.note) textarea.value = state.note;

    if (state.checked && isAccordionSavedOpen()) {
      // Embrulho ativo e accordion salvo como aberto
      trigger.classList.add('wt-collapse__trigger--active');
      trigger.setAttribute('aria-expanded', 'true');
    } else {
      trigger.classList.remove('wt-collapse__trigger--active');
      trigger.setAttribute('aria-expanded', 'false');
    }
  }

  // ─── Delegação de eventos no document (sobrevive a re-renders) ───────────────
  let listenersAttached = false;

  function attachListeners() {
    if (listenersAttached) return;
    listenersAttached = true;

    // Clique na seta: abre/fecha o accordion somente se o embrulho está ativo
    document.addEventListener('click', function (e) {
      var arrow = e.target.closest('.lh-gift-wrap__arrow');
      if (!arrow) return;

      e.preventDefault();
      e.stopPropagation();
      var checkbox = getCheckbox();
      if (!checkbox || !checkbox.checked) return;

      if (isAccordionOpen()) {
        closeAccordion();
        saveAccordionOpen(false);
      } else {
        openAccordion(false);
        saveAccordionOpen(true);
      }
    });

    // Clique no texto: toggle do embrulho (igual ao checkbox)
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('#lh-gift-wrap-trigger');
      if (!trigger) return;
      if (e.target.id === 'lh-gift-wrap-checkbox') return;
      if (e.target.closest('.lh-gift-wrap__arrow')) return;

      e.preventDefault();
      var checkbox = getCheckbox();
      if (!checkbox) return;
      checkbox.checked = !checkbox.checked;
      if (checkbox.checked) { activateGiftWrap(); }
      else                  { deactivateGiftWrap(); }
    });

    // Checkbox controla a ativação/desativação do embrulho
    document.addEventListener('change', function (e) {
      if (e.target.id !== 'lh-gift-wrap-checkbox') return;
      if (e.target.checked) { activateGiftWrap(); }
      else                  { deactivateGiftWrap(); }
    });

    // Digitação no textarea — persiste no localStorage
    document.addEventListener('input', function (e) {
      if (e.target.id !== 'lh-gift-wrap-note') return;
      const state = loadState();
      saveState(state.checked, e.target.value);
    });

    /**
     * Quando outro accordeon do grupo abre, fecha o painel do embrulho
     * sem alterar o estado (produto permanece no carrinho, checkbox permanece marcado).
     * Na próxima abertura do accordeon do embrulho, o estado é restaurado.
     */
    document.addEventListener('click', function (e) {
      const otherTrigger = e.target.closest(
        `collapsible-section[data-accordion-set="${ACCORDION_GROUP}"] .wt-collapse__trigger`
      );
      if (!otherTrigger || !isAccordionOpen()) return;

      // Verifica após o clique se o outro accordeon abriu (tinha estado fechado antes)
      const wasOpen = otherTrigger.classList.contains('wt-collapse__trigger--active');
      setTimeout(function () {
        const isNowOpen = otherTrigger.classList.contains('wt-collapse__trigger--active');
        if (!wasOpen && isNowOpen) {
          closeAccordion();
        }
      }, 10);
    });
  }

  // ─── MutationObserver: detecta re-renders do cart drawer ─────────────────────
  function observeCartDrawer() {
    const cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;

    // Sem debounce: MutationObserver roda antes do browser pintar,
    // então restaurar o estado aqui evita o flash visual.
    new MutationObserver(function () {
      if (document.getElementById('lh-gift-wrap-trigger')) {
        restoreState();
      }
    }).observe(cartDrawer, { childList: true, subtree: true });
  }

  // ─── Inicialização ───────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    attachListeners();
    restoreState();
    observeCartDrawer();
  });

})();
